import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Stack, Typography, Button, Checkbox,
  Table, TableHead, TableRow, TableCell, TableBody, alpha, useTheme,
  FormControl, Select, MenuItem, Skeleton, Tooltip, Chip, ToggleButton, ToggleButtonGroup,
  MenuItem as MItem, Menu, ListItemIcon, ListItemText, Slide, CircularProgress
} from '@mui/material';
import {
  PlaylistAddCheck, RestartAlt, FilterAlt,
  TrendingUp, EditNote, Save, UndoOutlined
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  PromotionPreviewRow, PromotionDecision, SessionClass, PromotionRun
} from '../../types/sessions';
import { promotionsApi } from '../../services/sessions/promotionsApi';
import CommitConfirmationDialog from './CommitConfirmationDialog';
import { useAppSelector } from '../../store/hooks';
import { getGradeColorFromPercentage } from '../../utils/gradeUtils';

interface DecisionsTableProps {
  run: PromotionRun;
  classId: string;
  className: string;
  classes: SessionClass[];
  onMutated: () => void;
}

type DecisionFilter = 'all' | 'undecided' | 'decided_only' | 'committed_only';

const isCommitted = (r: PromotionPreviewRow): boolean =>
  r.enrollment.enrollmentStatus !== 'enrolled';

type DecisionChoice = 'promote' | 'retain' | 'graduate' | 'drop';

const DECISION_OPTIONS: { value: DecisionChoice; label: string }[] = [
  { value: 'promote',  label: 'Promote' },
  { value: 'retain',   label: 'Retain' },
  { value: 'graduate', label: 'Graduate' },
  { value: 'drop',     label: 'Drop' },
];

const DecisionsTable: React.FC<DecisionsTableProps> = ({ run, classId, className, classes, onMutated }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  // School-configured grade bands — same source the Results page uses, so
  // chip colors stay consistent across both surfaces.
  const grades = useAppSelector((state) => state.grades.grades);

  const [rows, setRows] = useState<PromotionPreviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<DecisionFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<HTMLElement | null>(null);
  const [commitOpen, setCommitOpen] = useState<{ ids: string[] } | null>(null);
  // Staged-but-not-yet-persisted decision edits, keyed by enrollmentId. The
  // map is the only mutation surface for per-row Selects — actually hitting
  // the server happens in `saveChanges` (one bulk call), matching the
  // "edit locally, then Save" pattern used on the Class Teachers page.
  const [dirtyDecisions, setDirtyDecisions] = useState<Map<string, DecisionChoice>>(new Map());
  const [saving, setSaving] = useState(false);
  const hasUnsaved = dirtyDecisions.size > 0;

  const classLookup = useMemo(() => new Map(classes.map(c => [String(c._id), c])), [classes]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await promotionsApi.previewByClass(run._id, classId, 1, 500);
      setRows(result.data);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load decisions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [run._id, classId, enqueueSnackbar]);

  useEffect(() => { setSelectedIds(new Set()); setDirtyDecisions(new Map()); load(); }, [load]);

  // Effective decision for rendering: dirty edit (if any) wins over the
  // persisted effectiveDecision from the server.
  const decisionFor = useCallback(
    (row: PromotionPreviewRow): PromotionDecision => {
      const d = dirtyDecisions.get(String(row.enrollment._id));
      return d ?? row.effectiveDecision;
    },
    [dirtyDecisions]
  );

  const filteredRows = useMemo(() => {
    switch (filter) {
      case 'undecided':
        return rows.filter(r => !isCommitted(r) && decisionFor(r) === 'pending');
      case 'decided_only':
        return rows.filter(r => !isCommitted(r) && decisionFor(r) !== 'pending');
      case 'committed_only':
        return rows.filter(r => isCommitted(r));
      default:
        return rows;
    }
  }, [rows, filter, decisionFor]);

  const selectableRows = filteredRows.filter(r => !isCommitted(r) && decisionFor(r) !== 'pending');
  const allSelectableIds = selectableRows.map(r => String(r.enrollment._id));
  const allSelected = selectableRows.length > 0 && selectableRows.every(r => selectedIds.has(String(r.enrollment._id)));
  const someSelected = selectableRows.some(r => selectedIds.has(String(r.enrollment._id))) && !allSelected;

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allSelectableIds));
  };
  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  // Counts — reflect the staged view (dirty edits overlaid on persisted state)
  // so the toolbar tab labels stay honest while the user edits.
  const counts = useMemo(() => ({
    total:     rows.length,
    undecided: rows.filter(r => !isCommitted(r) && decisionFor(r) === 'pending').length,
    decided:   rows.filter(r => !isCommitted(r) && decisionFor(r) !== 'pending').length,
    committed: rows.filter(r => isCommitted(r)).length
  }), [rows, decisionFor]);

  // ── Decision changes ─────────────────────────────────────────────────
  // Per-row Select edits are staged locally. The server isn't touched until
  // the user clicks Save. If the user edits a row back to its persisted
  // value, we drop it from the dirty map so the Save bar dismisses cleanly.
  const stageDecision = (row: PromotionPreviewRow, decision: DecisionChoice) => {
    const id = String(row.enrollment._id);
    const persisted = row.effectiveDecision;
    setDirtyDecisions(prev => {
      const next = new Map(prev);
      if (decision === persisted) next.delete(id);
      else next.set(id, decision);
      return next;
    });
  };

  const discardChanges = () => {
    setDirtyDecisions(new Map());
    enqueueSnackbar('Unsaved changes discarded', { variant: 'info' });
  };

  const saveChanges = async () => {
    if (dirtyDecisions.size === 0) return;
    setSaving(true);
    try {
      const payload = Array.from(dirtyDecisions, ([enrollmentId, promotionDecision]) => ({
        enrollmentId,
        promotionDecision,
      }));
      await promotionsApi.applyDecisions(run._id, payload);
      enqueueSnackbar(
        `Saved ${payload.length} change${payload.length === 1 ? '' : 's'}`,
        { variant: 'success' }
      );
      setDirtyDecisions(new Map());
      await load();
      onMutated();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not save changes', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const resetSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await promotionsApi.resetDecisions(run._id, ids);
      enqueueSnackbar(`${ids.length} decision${ids.length === 1 ? '' : 's'} reset`, { variant: 'success' });
      setSelectedIds(new Set());
      await load();
      onMutated();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not reset decisions', { variant: 'error' });
    }
  };

  const promoteAllUndecided = () => {
    const targets = rows.filter(r => !isCommitted(r) && decisionFor(r) === 'pending');
    if (targets.length === 0) {
      enqueueSnackbar('No undecided rows', { variant: 'info' });
      return;
    }
    // Stage into the dirty map — the user reviews + Saves once, same as
    // per-row edits.
    setDirtyDecisions(prev => {
      const next = new Map(prev);
      for (const r of targets) {
        next.set(String(r.enrollment._id), 'promote');
      }
      return next;
    });
    setBulkMenuAnchor(null);
    enqueueSnackbar(
      `${targets.length} row${targets.length === 1 ? '' : 's'} staged — Save to apply`,
      { variant: 'info' }
    );
  };

  // ── Render ───────────────────────────────────────────────────────────
  // Commit only ever acts on PERSISTED decisions, never on staged edits.
  // Counts reflect staged view (so the tab labels stay accurate), but
  // canCommitAll falls back to persisted state and is hard-disabled while
  // any local edits are unsaved.
  const persistedDecidedCount = useMemo(
    () => rows.filter(r => !isCommitted(r) && r.effectiveDecision !== 'pending').length,
    [rows]
  );
  const persistedUndecidedCount = useMemo(
    () => rows.filter(r => !isCommitted(r) && r.effectiveDecision === 'pending').length,
    [rows]
  );
  const canCommitAll = !hasUnsaved && persistedUndecidedCount === 0 && persistedDecidedCount > 0;

  if (loading) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={56} />
        ))}
      </Stack>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1.75 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterAlt fontSize="small" sx={{ color: 'text.secondary' }} />
          <ToggleButtonGroup
            value={filter}
            exclusive
            size="small"
            onChange={(_, v) => v && setFilter(v as DecisionFilter)}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, fontWeight: 500 } }}
          >
            <ToggleButton value="all">All ({counts.total})</ToggleButton>
            <ToggleButton value="undecided">Undecided ({counts.undecided})</ToggleButton>
            <ToggleButton value="decided_only">Decided ({counts.decided})</ToggleButton>
            <ToggleButton value="committed_only">Committed ({counts.committed})</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<PlaylistAddCheck />}
            variant="outlined"
            onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Bulk actions
          </Button>
          <Menu
            anchorEl={bulkMenuAnchor}
            open={Boolean(bulkMenuAnchor)}
            onClose={() => setBulkMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                elevation: 3,
                sx: {
                  mt: 0.5,
                  minWidth: 280,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                },
              },
            }}
          >
            <MItem
              onClick={promoteAllUndecided}
              sx={{ py: 1.25, px: 2, gap: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TrendingUp fontSize="small" />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Mark all undecided as Promote"
                secondary="Stages a Promote decision for every row without one"
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                secondaryTypographyProps={{ fontSize: '0.72rem', sx: { mt: 0.25 } }}
              />
            </MItem>
          </Menu>
        </Stack>
      </Stack>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.025) }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={selectableRows.length === 0}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Student</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Result</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Decision</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Target class</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    No rows match the current filter.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredRows.map(row => {
              const id = String(row.enrollment._id);
              const committed = isCommitted(row);
              const stagedDecision = decisionFor(row);
              const isDirty = dirtyDecisions.has(id);
              // Target class follows the persisted effectiveTargetClassId
              // for now — recomputing target on staged decisions would need
              // the backend resolver, which only runs on save.
              const targetClass = row.effectiveTargetClassId
                ? classLookup.get(String(row.effectiveTargetClassId))
                : null;
              const isTouched = row.enrollment.decisionTouched;

              return (
                <TableRow
                  key={id}
                  hover
                  sx={{
                    bgcolor: committed ? alpha(theme.palette.text.primary, 0.025) : 'inherit',
                    opacity: committed ? 0.72 : 1
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(id)}
                      onChange={() => toggleOne(id)}
                      disabled={committed || stagedDecision === 'pending' || isDirty}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.studentName ?? '—'}</Typography>
                    <Chip
                      label={[
                        `Roll ${row.enrollment.rollNumber}`,
                        row.sourceSectionName || null,
                      ].filter(Boolean).join(' · ')}
                      size="small"
                      variant="outlined"
                      sx={{
                        mt: 0.5,
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 500,
                        letterSpacing: 0.2,
                        borderColor: alpha(theme.palette.primary.main, 0.35),
                        color: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        '& .MuiChip-label': { px: 0.85 },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {row.averagePercentage === null || row.averagePercentage === undefined ? (
                      <Chip
                        label="Pending"
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary' }}
                      />
                    ) : (
                      <Chip
                        label={`${row.averagePercentage.toFixed(1)}%`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: '#fff',
                          backgroundColor: getGradeColorFromPercentage(row.averagePercentage, grades),
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <FormControl
                        size="small"
                        sx={{
                          minWidth: 150,
                          ...(isDirty && {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.warning.main,
                            },
                          }),
                        }}
                        disabled={committed}
                      >
                        <Select
                          value={stagedDecision === 'pending' ? '' : stagedDecision}
                          onChange={(e) => stageDecision(row, e.target.value as DecisionChoice)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            <em style={{ color: theme.palette.text.disabled }}>Decide…</em>
                          </MenuItem>
                          {DECISION_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {isDirty && (
                        <Tooltip title="Unsaved change">
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'warning.main' }} />
                        </Tooltip>
                      )}
                      {!isDirty && isTouched && !committed && (
                        <Tooltip title="Manually set — auto-decide will skip this row">
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {targetClass ? (
                      <Typography variant="body2" fontWeight={500}>{targetClass.name}</Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      // One compact pill, tone driven by state. Tinted background
                      // + matching darker text reads as a status badge without
                      // shouting (no emoji, no heavy borders).
                      const base = {
                        height: 22,
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        letterSpacing: 0.25,
                        border: 0,
                        '& .MuiChip-label': { px: 1 },
                      } as const;
                      if (committed) {
                        return (
                          <Chip
                            size="small"
                            label={row.enrollment.enrollmentStatus.replace(/_/g, ' ')}
                            sx={{
                              ...base,
                              textTransform: 'capitalize',
                              bgcolor: alpha(theme.palette.success.main, 0.12),
                              color: theme.palette.success.dark,
                            }}
                          />
                        );
                      }
                      if (isDirty) {
                        return (
                          <Chip
                            size="small"
                            label="Unsaved"
                            sx={{
                              ...base,
                              bgcolor: alpha(theme.palette.warning.main, 0.14),
                              color: theme.palette.warning.dark,
                            }}
                          />
                        );
                      }
                      if (stagedDecision === 'pending') {
                        return (
                          <Chip
                            size="small"
                            label="Awaiting decision"
                            sx={{
                              ...base,
                              bgcolor: alpha(theme.palette.text.primary, 0.06),
                              color: 'text.secondary',
                            }}
                          />
                        );
                      }
                      return (
                        <Chip
                          size="small"
                          label="Ready"
                          sx={{
                            ...base,
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.dark,
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Footer commit bar */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {selectedIds.size > 0
              ? `${selectedIds.size} selected`
              : counts.decided > 0
                ? `${counts.decided} decided in this class`
                : 'No rows decided yet'}
          </Typography>
          {selectedIds.size > 0 && (
            <Tooltip title={hasUnsaved ? 'Save or discard your changes first' : ''}>
              <span>
                <Button
                  size="small"
                  startIcon={<RestartAlt />}
                  variant="text"
                  onClick={resetSelected}
                  disabled={hasUnsaved}
                >
                  Reset to default
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>

        <Stack direction="row" spacing={1}>
          <Tooltip title={hasUnsaved ? 'Save or discard your changes first' : ''}>
            <span>
              <Button
                variant="outlined"
                disabled={selectedIds.size === 0 || hasUnsaved}
                onClick={() => setCommitOpen({ ids: Array.from(selectedIds) })}
              >
                Commit selected ({selectedIds.size})
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={hasUnsaved ? 'Save or discard your changes first' : (!canCommitAll ? 'Resolve all undecided rows first' : '')}>
            <span>
              <Button
                variant="contained"
                disableElevation
                disabled={!canCommitAll}
                onClick={() => setCommitOpen({ ids: [] })}
              >
                Commit all decided in {className}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Floating Save bar — only renders when staged edits exist. Mirrors
          the Class Teachers page pattern: edits are local-only, persist on
          Save. */}
      <Slide direction="up" in={hasUnsaved} mountOnEnter unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: theme.zIndex.snackbar,
            px: 2,
            py: 1.25,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
            bgcolor: 'background.paper',
            boxShadow: `0 12px 32px -8px ${alpha(theme.palette.common.black, 0.18)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              color: 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <EditNote fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
              {dirtyDecisions.size} unsaved change{dirtyDecisions.size === 1 ? '' : 's'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Edits are kept locally — click Save to apply.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
            <Button
              size="small"
              startIcon={<UndoOutlined />}
              onClick={discardChanges}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              Discard
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
              onClick={saveChanges}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Paper>
      </Slide>

      {commitOpen && (
        <CommitConfirmationDialog
          open={Boolean(commitOpen)}
          runId={run._id}
          classId={classId}
          className={className}
          enrollmentIds={commitOpen.ids}
          rows={commitOpen.ids.length > 0
            ? rows.filter(r => commitOpen.ids.includes(String(r.enrollment._id)))
            : rows.filter(r => !isCommitted(r) && r.effectiveDecision !== 'pending')
          }
          classes={classes}
          onClose={() => setCommitOpen(null)}
          onCommitted={() => { setSelectedIds(new Set()); load(); onMutated(); }}
        />
      )}
    </Box>
  );
};

export default DecisionsTable;
